import React, { useState, useMemo, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CommonFileUploader from '../../../fileupload/CommonFileUploader/CommonFileUploader';
import { updateGeneralData } from '../../../../actions/timesheet';
import { tneAxiosClient } from '../../../../api/apiSettings';
import { responsiveSizeTimesheet, timesheetsStatuses } from '../../../../utils/constants';
import { useWindowSize } from '../../../../utils/hooks';
import AddAttachmentIcon from '../../../common/Icons/AddAttachmentIcon';
import MainButton from '../../../common/buttons/MainButton';
import BaseSwipeableDrawer from '../../../common/BaseSwipeableDrawer/BaseSwipeableDrawer';
import { basicErrorHandler } from '../../../../utils/helpers';
import CustomDocViewer from '../../../common/CustomDocViewer/CustomDocViewer';
import colors from '../../../../scss/colors';
import classes from './Attachments.module.scss';

export function AttachmentItem({ label, deleteHandler, deletePending, clickHandler }) {
  return (
    <div className={classes.attachItem}>
      <p role="presentation" onClick={clickHandler} className={deletePending ? classes.deleting : ''}>
        {label}
      </p>
      {!deletePending && <CloseIcon onClick={deleteHandler} sx={{ color: colors.ICON_GREY, fontSize: '16px' }} />}
    </div>
  );
}

function AttachmentsInfo({ attachments, clickHandler }) {
  const { t } = useTranslation();
  const size = useWindowSize();
  const [open, setOpen] = useState(false);

  return (
    <>
      <div role="presentation" className={classes.infoContainer} onClick={() => setOpen(true)}>
        {size.width < responsiveSizeTimesheet && <AddAttachmentIcon />}
        <div>
          {t('dashboard.timesheet.attachments.attachmentstitle')}&nbsp;
          <span className={classes.number}>({attachments.length})</span>
        </div>
        {size.width < responsiveSizeTimesheet && <ArrowForwardIosIcon sx={{ opacity: 0.1 }} />}
      </div>
      {open && size.width > responsiveSizeTimesheet ? (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          sx={{
            '& .MuiPaper-root.MuiDialog-paper': {
              maxWidth: '100%',
              margin: '16px',
              padding: '36px 36px 72px 36px'
            },
            '& .MuiDialog-paper': {
              borderRadius: '20px',
              overflowY: 'visible',
              paddingBottom: '48px',
              backgroundColor: colors.PAGE_BACKGROUND
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p className={classes.dialogHeader}>{t('dashboard.timesheet.attachments.attachmentstitle')}</p>
            <div role="presentation" onClick={() => setOpen(false)} style={{ cursor: 'pointer' }}>
              <CloseIcon sx={{ color: colors.MEDIUM_GREY, fontSize: '32px' }} />
            </div>
          </DialogTitle>
          <div className={classes.attachmentsContainer}>
            {attachments.map((attach) => {
              return (
                <div role="presentation" onClick={() => clickHandler(attach)} key={attach.Id}>
                  {attach.FileAttachmentName}
                </div>
              );
            })}
          </div>
        </Dialog>
      ) : (
        <BaseSwipeableDrawer open={open} closeHandler={() => setOpen(false)}>
          <div className={classes.swiper} />
          <p className={classes.attachmentsLabel}>{t('dashboard.timesheet.attachments.attachmentstitle')}</p>
          <div className={classes.attachmentsContainer}>
            {attachments.map((attach) => {
              return (
                <div role="presentation" onClick={() => clickHandler(attach)} key={attach.Id}>
                  {attach.FileAttachmentName}
                </div>
              );
            })}
          </div>
          <MainButton size="small" variant="primary" rounded forceWidth="204px" action={() => setOpen(false)}>
            {t('common.general.close')}
          </MainButton>
        </BaseSwipeableDrawer>
      )}
    </>
  );
}

export default function Attachments({ status, timesheetId, attachments, editeMode, handleChanges }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { width } = useWindowSize();

  const canBeEdited = useMemo(
    () => ![timesheetsStatuses(t).approved.label, timesheetsStatuses(t).processed.label].includes(status),
    [status]
  );

  const [loading, setLoading] = useState(false);
  const [deletePending, setDeletePending] = useState(null);
  const [opening, setOpening] = useState(false);
  const [attachment, setAttachment] = useState({
    show: false,
    id: '',
    filename: '',
    data: null
  });

  const handleAttachmentViewer = async (file) => {
    try {
      if (file?.Id) {
        setOpening(true);
        const result = await tneAxiosClient.get(`v1/timesheet/download-attachment?timesheetAttachmentId=${file?.Id}`);
        if (result.status === 200) {
          setAttachment({
            show: true,
            id: file?.Id,
            filename: file?.FileAttachmentName,
            data: result?.data?.Data
          });
        }
        setOpening(false);
      }
    } catch (e) {
      setOpening(false);
      basicErrorHandler(dispatch, e.message);
    }
  };

  const handleDownload = useCallback(() => {
    const link = document.createElement('a');
    link.href = attachment.data;
    link.download = attachment.filename;
    link.click();
  }, [attachment.filename]);

  const uploadHandler = async (file) => {
    const data = new FormData();
    data.append('attachment', file[0]);
    try {
      setLoading(true);
      const result = await tneAxiosClient.post(`v1/timesheet/attachment/${timesheetId}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const newAttachmentsList = [...attachments, { FileAttachmentName: file[0].name, Id: result.data.Data.Id }];
      dispatch(updateGeneralData('TimesheetAttachments', newAttachmentsList));
      // TODO This is temporary solution to improve UX little bit
      handleChanges(true);
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setLoading(false);
  };

  const deleteHandler = async (fileId) => {
    try {
      setDeletePending(fileId);
      await tneAxiosClient.delete(`v1/timesheet/attachment/${fileId}`);
      const newAttachmentsList = attachments.filter((item) => item.Id !== fileId);
      dispatch(updateGeneralData('TimesheetAttachments', newAttachmentsList));
      // TODO This is temporary solution to improve UX little bit
      handleChanges(true);
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setDeletePending(null);
  };

  return (
    <div className={`${classes.uploadSection} ${!canBeEdited ? classes.withBottomGap : ''}`}>
      {editeMode && canBeEdited ? (
        <>
          <CommonFileUploader
            uploadHandler={uploadHandler}
            isMultiple={false}
            acceptedFormats={['.jpeg', '.jpg', '.png', '.pdf', '.doc', '.docx', '.csv', '.xls', 'xlsx']}
            maxFileSizeMB={5}
            allowedQtyOfAttachments={10}
            existingAttachmentsQty={attachments.length}
            loading={loading}
          />
          <div className={classes.attachmentsList}>
            {attachments?.map((attach, index) => (
              <>
                <AttachmentItem
                  key={attach.Id}
                  clickHandler={() => handleAttachmentViewer(attach)}
                  label={`${attach.FileAttachmentName}`}
                  deleteHandler={() => deleteHandler(attach.Id)}
                  deletePending={attach.Id === deletePending}
                />
                {index !== attachments.length - 1 && width < responsiveSizeTimesheet && (
                  <div className={classes.divider}>
                    <div />
                  </div>
                )}
              </>
            ))}
          </div>
        </>
      ) : (
        <AttachmentsInfo attachments={attachments} clickHandler={handleAttachmentViewer} />
      )}
      {(attachment?.show || opening) && (
        <CustomDocViewer
          settings={attachment}
          handleDownload={handleDownload}
          close={() =>
            setAttachment({
              show: false,
              id: '',
              filename: '',
              data: null
            })
          }
          isLoading={opening}
        />
      )}
    </div>
  );
}

// TODO ADD size to name when backend will start to send filesize {(${(attach.size / 1024 / 1024).toFixed(1)}Mb)}
